import { useState } from "react";

import { formatPublicSourceLabel } from "@/features/machines/addressing";
import { isNativeCumulativeSource, resolveProductionTotalMode } from "../../productionSources";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import type { WidgetHistoryState } from "../../useProductionHistories";
import type { WidgetProductionTrackerState } from "../../useProductionTrackers";
import { describeSignalDiagnostic } from "./diagnosticMeaning";
import {
  formatWidgetNumber,
  formatWidgetRelative,
  getFooterStatus,
} from "./format";
import { WidgetFooter, WidgetShell } from "./WidgetShell";

interface ProductionKpiWidgetProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  history?: WidgetHistoryState;
  productionTracker?: WidgetProductionTrackerState;
  onResetProduction?: () => Promise<void>;
  editable?: boolean;
  onConfigure?: () => void;
  onGenerateChart?: () => void;
}

export function ProductionKpiWidget({
  widget,
  value,
  history,
  productionTracker,
  onResetProduction,
  editable,
  onConfigure,
  onGenerateChart,
}: ProductionKpiWidgetProps) {
  const [confirmingReset, setConfirmingReset] = useState(false);
  // Layout legado fica salvo, mas nao pode continuar multiplicando um total
  // nativo por 60 nem apresentar historico/tracker do acc como se fosse de v.
  if (isNativeCumulativeSource(widget.source, widget.settings.signalKind)) {
    return (
      <WidgetShell
        title={widget.title}
        tone="warn"
        bodyLayout="col"
        editable={editable}
        onConfigure={onConfigure}
        onGenerateChart={onGenerateChart}
      >
        <div className="num-v">--</div>
        <div className="production-kpi__meta">
          Configuração incompatível: acumulado nativo não é ritmo. Escolha Número ou Histórico.
        </div>
      </WidgetShell>
    );
  }
  const production = widget.settings.production ?? { mode: "rate" };
  const multiplier = production.mode === "hourly" ? production.multiplier ?? 60 : 1;
  const historicalWindow =
    production.mode === "total" && production.window && production.window !== "current"
      ? production.window
      : null;
  const historicalValue =
    historicalWindow && history?.status === "ready" ? history.data.total : null;
  const totalMode = resolveProductionTotalMode(production);
  const expectsTracker =
    production.mode === "total" &&
    !historicalWindow &&
    totalMode !== "physical";
  const trackerData =
    productionTracker?.status === "ready" || productionTracker?.status === "resetting"
      ? productionTracker.data
      : null;
  const numeric = trackerData
    ? trackerData.currentAmount
    : expectsTracker
      ? null
      : historicalWindow
      ? historicalValue
      : value.isAvailable && value.numericValue !== null
        ? value.numericValue * multiplier
        : null;
  const decimals = widget.settings.decimals ?? (production.mode === "total" ? 0 : 1);
  const unit = resolveProductionUnit(widget);
  const target = trackerData?.target ?? production.target;
  const hasTarget = typeof target === "number" && target > 0;
  const progress = trackerData?.progressPercent ??
    (numeric !== null && target && target > 0
      ? Math.max(0, Math.min(100, (numeric / target) * 100))
      : null);
  const shellTone = historicalWindow
    ? history?.status === "error"
      ? "warn"
      : "ok"
    : value.tone === "danger"
      ? "danger"
      : value.tone === "warn"
        ? "warn"
        : "ok";
  const measuredAt = trackerData?.lastMeasuredAt ??
    (historicalWindow && history?.status === "ready" ? history.data.to : value.measuredAt);
  const footerStatus = historicalWindow
    ? history?.status === "ready"
      ? "ok"
      : history?.status === "error"
        ? "warn"
        : "muted"
    : getFooterStatus(value);
  const footerLabel = historicalWindow
    ? history?.status === "ready"
      ? "HISTORICO"
      : history?.status === "unavailable"
        ? "REQUER TIMESCALE"
        : history?.status === "error"
          ? "ERRO"
          : "CARREGANDO"
    : getProductionToneLabel(value);
  // Em leitura ao vivo, falha AIN/MB descreve o SINAL e deve vencer frases de
  // processo como "ritmo atual". Um total historico, por outro lado, continua
  // valido mesmo se a leitura instantanea estiver em falha neste momento.
  const diagnostic = historicalWindow
    ? null
    : describeSignalDiagnostic(value.diagnostic);

  return (
    <WidgetShell
      title={widget.title}
      subtitle={formatPublicSourceLabel(value.source, widget.settings.signalKind)}
      tone={shellTone}
      bodyLayout="col"
      size={widget.layout.size}
      alarmEnabled={widget.settings.alarmEnabled}
      editable={editable}
      onConfigure={onConfigure}
      onGenerateChart={onGenerateChart}
      footer={
        <WidgetFooter
          ts={formatWidgetRelative(measuredAt)}
          measuredAt={measuredAt}
          source={value.source}
          status={footerStatus}
          statusLabel={footerLabel}
        />
      }
    >
      <div className="production-kpi">
        <div className="num-v">
          {formatWidgetNumber(numeric, decimals)}
          {unit ? <span className="u">{unit}</span> : null}
        </div>
        <div className="production-kpi__meta">
          {diagnostic
            ? diagnostic.text
            : trackerData
            ? hasTarget
              ? `lote atual · ${trackerData.completedCycles} ciclo(s) concluído(s)`
              : `desde o último zeramento · iniciado ${formatWidgetRelative(trackerData.startedAt)}`
            : expectsTracker && productionTracker?.status === "error"
              ? "não foi possível carregar o acumulado zerável"
              : expectsTracker && productionTracker?.status === "unavailable"
                ? "zeramento requer Timescale"
                : expectsTracker
                  ? "preparando acumulado zerável"
                  : describeProductionMode(production.mode, historicalWindow)}
        </div>
        {historicalWindow && history?.status === "unavailable" ? (
          <div className="production-kpi__meta">ative o Timescale para esta janela</div>
        ) : null}
        {progress !== null ? (
          <div className="production-kpi__target">
            <div className="availability__bar" aria-hidden="true">
              <span style={{ width: `${progress}%` }} />
            </div>
            <span>{formatWidgetNumber(progress, 0)}% da meta</span>
          </div>
        ) : null}
        {trackerData ? (
          <div className="production-kpi__cycle">
            {hasTarget ? (
              <span>
                Faltam {formatWidgetNumber(trackerData.remainingAmount, decimals)}
                {unit ? ` ${unit}` : ""}
              </span>
            ) : (
              <span>Total físico preservado no equipamento</span>
            )}
            <span>
              {trackerData.manualResets} reset(s) · {trackerData.interruptedCycles} interrupção(ões)
            </span>
            {onResetProduction && !editable && !confirmingReset ? (
              <button
                type="button"
                className="production-kpi__reset"
                disabled={productionTracker?.status === "resetting"}
                onClick={() => setConfirmingReset(true)}
              >
                {productionTracker?.status === "resetting"
                  ? "Zerando..."
                  : hasTarget
                    ? "Zerar lote"
                    : "Zerar metragem"}
              </button>
            ) : null}
            {onResetProduction && !editable && confirmingReset ? (
              <div
                className="production-kpi__reset-confirm"
                role="group"
                aria-label="Confirmar reset analítico do lote"
              >
                <span>
                  {hasTarget
                    ? "Iniciar um novo lote? O total físico será preservado."
                    : "Zerar a metragem exibida a partir de agora? O total físico será preservado."}
                </span>
                <div>
                  <button type="button" onClick={() => setConfirmingReset(false)}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="is-danger"
                    disabled={productionTracker?.status === "resetting"}
                    onClick={() => {
                      void onResetProduction().finally(() => setConfirmingReset(false));
                    }}
                  >
                    {productionTracker?.status === "resetting"
                      ? "Zerando..."
                      : "Confirmar reset"}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </WidgetShell>
  );
}

function resolveProductionUnit(widget: DashboardWidget): string | undefined {
  if (!widget.settings.unit) return undefined;
  const unit = widget.settings.unit.trim();
  if (!unit) return undefined;
  if (widget.settings.production?.mode === "rate" && !widget.settings.unit.includes("/")) {
    return `${unit}/min`;
  }
  if (widget.settings.production?.mode === "hourly") {
    if (/\/min$/i.test(unit)) return unit.replace(/\/min$/i, "/h");
    if (/\/h$/i.test(unit)) return unit;
    if (unit.includes("/")) return unit;
    return `${unit}/h`;
  }
  return unit;
}

function describeProductionMode(
  mode: NonNullable<DashboardWidget["settings"]["production"]>["mode"],
  window: string | null,
): string {
  if (mode === "hourly") return "projeção por hora";
  if (mode === "total") return window ? `total nas últimas ${window}` : "total acumulado";
  return "ritmo atual do equipamento";
}

function getProductionToneLabel(value: ResolvedWidgetValue): string {
  if (!value.isAvailable) return "SEM SINAL";
  if (value.tone === "warn") return "ALERTA";
  if (value.tone === "danger") return "PERIGO";
  return "OK";
}
